<template>
  <v-card id="account-length-group-form" >
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span class="headline">
            {{ title }}
          </span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideRefresh hideFullscreen/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row>
        <v-col cols="10">
          <v-text-field
            id="length-group-label"
            ref="lengthGroupLabel"
            :maxLength="maxLabelLength"
            :label="$t('label')"
            v-model="lengthGroupBuilder.label"
            :rules="[rules.required, noDuplicateGroupLabelsFieldRule, validLabelLength]">
          </v-text-field>
        </v-col>

        <v-col cols="10">
          <v-select
          id="length-group-length-selector"
          :label="$t('lengths')"
          @change="sortLengths"
          return-object
          multiple
          item-text="length"
          :rules="[rules.requiredSelect, noDuplicateLengthGroupsFieldRule]"
          v-model="lengthGroupBuilder.logLengths"
          :items="accountLogLengths"
          :menu-props="{ offsetY: true }">
            <template #item="{ item }">
              <div @click="handleLengthItemClick(item, $event)" style="width: 100%; display: flex; align-items: center;">
                <v-icon
                  v-if="lengthGroupBuilder.logLengths?.some(l => l.length === item.length)" color="primary">mdi-checkbox-marked
                </v-icon>
                <v-icon v-else>
                  mdi-checkbox-blank-outline
                </v-icon>
                <span class="ml-2">{{ item.length }}'</span>
              </div>
            </template>
            <template #selection="{ item }">
              <v-chip
              close
              outlined
              label
              class="grey-chip"
              close-icon="mdi-close"
              @click:close="lengthRemovedFromGroup(item)">
                <span>{{ item.length }}'</span>
              </v-chip>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="py-4 mx-0">
      <v-row justify="space-between">
        <v-col cols="auto">
          <v-btn color="primary" outlined @click="handleCancel" tabindex="-1">
            <span>
              {{ $t('cancel') }}
            </span>
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn color="primary" id="save" :disabled="!lengthGroupValid" @click="handleSave">
            <span>
              {{ $t('save') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>
<script>
import fieldRules from '@/utils/rules.js'
import { isEqual, cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import { shiftSelectClick } from '@/utils/shiftSelect'

const MAX_LABEL_LENGTH = 32

export default {
  name: 'AccountLengthGroupForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    lengthGroup: {
      type: Object,
      required: false,
      default: () => ({
        label: '',
        logLengths: []
      })
    }
  },

  data: () => ({
    lengthGroupBuilder: {
      label: '',
      logLengths: []
    },
    maxLabelLength: MAX_LABEL_LENGTH,
    rules: fieldRules.rules,
    lastSelectedIndex: null
  }),

  beforeDestroy () {
    document.removeEventListener('keyup', this.onKeyup)
  },

  mounted () {
    if (this.lengthGroup) this.lengthGroupBuilder = cloneDeep(this.lengthGroup)
    document.addEventListener('keyup', this.onKeyup)
    this.$nextTick(() => {
      this.focus('lengthGroupLabel')
    })
  },

  computed: {
    ...mapGetters('account-scaling', ['accountLogLengths', 'accountLengthGroups']),

    title () {
      return this.lengthGroupBuilder?.lengthGroupId
        ? this.$t('editingLengthGroup')
        : this.$t('creatingLengthGroup')
    },

    lengthGroupValid () {
      return this.hasLengths &&
        this.hasLabel &&
        this.noDuplicateGroupLabels &&
        this.noDuplicateLengthGroups
    },

    hasLengths () {
      return this.lengthGroupBuilder?.logLengths?.length > 0
    },

    hasLabel () {
      return this.lengthGroupBuilder?.label?.length > 0
    },

    noDuplicateGroupLabels () {
      return this.accountLengthGroups
        .every(lengthGroup => lengthGroup?.label !== this.lengthGroupBuilder?.label ||
          this.isBeingEdited(lengthGroup))
    },

    noDuplicateLengthGroups () {
      return this.accountLengthGroups
        .every(lengthGroup => this.uniqueLengthGroup(lengthGroup) ||
          this.isBeingEdited(lengthGroup))
    }
  },

  methods: {
    focus (refString) {
      this.$nextTick(_ => {
        this.$refs[refString].focus()
      })
    },

    validLabelLength (string) {
      return !string ||
        string.length <= MAX_LABEL_LENGTH ||
        this.$t('cannotExceedNCharacters', { n: MAX_LABEL_LENGTH })
    },

    handleSave () {
      if (this.lengthGroupValid) {
        this.$emit('save', this.lengthGroupBuilder)
      } else {
        this.setSnackError(this.$t('lengthGroupNotValid'))
      }
    },

    sortLengths (lengths) {
      lengths.sort((a, b) => a.length - b.length)
    },

    noDuplicateGroupLabelsFieldRule () {
      return this.noDuplicateGroupLabels ||
          this.$t('cannotDuplicateLabel')
    },

    noDuplicateLengthGroupsFieldRule () {
      return this.noDuplicateLengthGroups ||
          this.$t('cannotDuplicateLengthGroup')
    },

    uniqueLengthGroup (group) {
      const lengths = group.logLengths.map(ll => ll.length)
      lengths.sort()
      const editingLengths = this.lengthGroupBuilder.logLengths.map(ll => ll.length)
      editingLengths.sort()
      return !isEqual(lengths, editingLengths)
    },

    lengthRemovedFromGroup (length) {
      this.lengthGroupBuilder.logLengths = this.lengthGroupBuilder?.logLengths.filter(l => l.length !== length.length)
    },

    handleCancel () {
      this.$emit('cancel')
    },

    onKeyup (e) {
      if (this.inputIsActive()) {
        return
      }
      if (e.key.toLowerCase() === 'enter' && e.target?.id !== 'save') {
        this.handleSave()
      }
    },

    inputIsActive () {
      const activeElement = document.activeElement
      return ['length-group-length-selector', 'length-group-label'].includes(activeElement.id)
    },

    isBeingEdited (lengthGroup) {
      return this.lengthGroup?.lengthGroupId === lengthGroup?.lengthGroupId
    },

    handleLengthItemClick (item, event) {
      const { selection, anchor } = shiftSelectClick(
        this.accountLogLengths, this.lengthGroupBuilder.logLengths, this.lastSelectedIndex, item, event
      )
      this.lastSelectedIndex = anchor
      if (selection !== null) {
        selection.sort((a, b) => a.length - b.length)
        this.lengthGroupBuilder.logLengths = selection
      }
    }
  }
}
</script>
